/* Timbrica — /space-weather ("магнитная буря" / geomagnetic-storm dashboard).
 *
 * Pure vanilla JS. Reads window.SW_CFG (locale, dir, t-strings, same-origin API
 * urls, tile cfg, map home) and renders:
 *   • a banded Kp dial (0–9) with the live "storm: yes/no" verdict, WHEN it
 *     started or when the next one is due, and the NOAA R/S/G scale pills
 *   • the place: one city (search, geolocation, profile) for the whole page —
 *     every time is on its clock, plus what the storm means there (Kp is the
 *     same everywhere; whether an aurora reaches it is not)
 *   • the hour grid: today and the next days by three-hour period, each with
 *     its local hour and Kp — readable on a phone, unlike the chart's hover
 *   • 27 days ahead from NOAA's weekly outlook, the first days told by the
 *     fresher three-day forecast
 *   • solar wind — speed / density / Bz / Bt — each with an hour of sparkline
 *   • a 6-day Kp chart: observed (3 d) + NOAA forecast (3 d), storm gridlines,
 *     a "now" divider and a screen-reader summary
 *   • a Leaflet dark map painting the NOAA OVATION aurora oval, north OR south,
 *     with a location-based "will I see it tonight?" readout
 *   • the Sun's own numbers (X-ray class, 24 h flare tally, F10.7, sunspots)
 *   • NOAA's issued alerts, rendered from structured fields in the page locale
 * Data comes only from our same-origin proxies (RU-safe). Auto-refreshes.
 */
(function () {
  'use strict';
  var C = window.SW_CFG || {};
  var T = C.t || {};
  var API = C.api || {};
  var tr = function (k) { return T[k] != null ? T[k] : k; };
  var $ = function (id) { return document.getElementById(id); };
  var ae = function (ev, p) { try { window.__ae && window.__ae(ev, p || {}); } catch (e) {} };
  var stage = function (n) { try { window.__stage && window.__stage(n); } catch (e) {} };
  var PT = window.__placeTime, GM = window.__geomag;
  var SLUG = 'space-weather';
  var place = null;      // { lat, lon, name, tz } — one choice for the whole page
  var lastNow = null;    // the last payload, re-rendered when the place changes
  var lastFcst = null;   // OVATION forecast time, same reason
  var REDUCED = false;
  try { REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  // ── Kp → band ───────────────────────────────────────────────────────────
  // Kp 5..9 == storms G1..G5; below 5 is quiet / unsettled / active.
  var BANDS = [
    { to: 3, key: 'quiet',  g: 0 },
    { to: 4, key: 'quiet',  g: 0 },
    { to: 5, key: 'active', g: 0 },
    { to: 6, key: 'g1',     g: 1 },
    { to: 7, key: 'g2',     g: 2 },
    { to: 8, key: 'g3',     g: 3 },
    { to: 9, key: 'g4',     g: 4 },
    { to: 10, key: 'g5',    g: 5 }
  ];
  var cssVar = function (name, fallback) {
    try {
      var v = getComputedStyle(document.documentElement).getPropertyValue(name);
      if (!v) {
        var host = document.querySelector('.sw');
        if (host) v = getComputedStyle(host).getPropertyValue(name);
      }
      return (v || '').trim() || fallback;
    } catch (e) { return fallback; }
  };
  function bandKey(kp) {
    if (kp >= 9) return 'g5';
    if (kp >= 8) return 'g4';
    if (kp >= 7) return 'g3';
    if (kp >= 6) return 'g2';
    if (kp >= 5) return 'g1';
    if (kp >= 4) return 'active';
    return 'quiet';
  }
  function kpColor(kp) { return cssVar('--sw-' + bandKey(kp), '#2fb98a'); }
  function kpInk(kp) { return cssVar('--sw-' + bandKey(kp) + '-ink', '#4adea0'); }
  function gLevel(kp) { return kp >= 5 ? Math.min(5, Math.floor(kp) - 4) : 0; }
  function fmt(v, d) { return (v == null || isNaN(v)) ? '—' : Number(v).toFixed(d == null ? 0 : d); }
  function kpText(kp) {
    return fmt(kp, (kp != null && kp % 1) ? 1 : 0);
  }

  // ── SVG dial ────────────────────────────────────────────────────────────
  var CX = 130, CY = 132, R = 100;
  function mk(t) { return document.createElementNS('http://www.w3.org/2000/svg', t); }
  function polar(cx, cy, r, deg) {
    var a = deg * Math.PI / 180;
    return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
  }
  function arcPath(cx, cy, r, startDeg, endDeg) {
    var s = polar(cx, cy, r, startDeg), e = polar(cx, cy, r, endDeg);
    var large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    var sweep = endDeg < startDeg ? 1 : 0; // clockwise for decreasing angle
    return 'M' + s[0].toFixed(1) + ' ' + s[1].toFixed(1) +
      ' A' + r + ' ' + r + ' 0 ' + large + ' ' + sweep + ' ' + e[0].toFixed(1) + ' ' + e[1].toFixed(1);
  }
  var degFor = function (kp) { return 180 - (Math.max(0, Math.min(9, kp)) / 9) * 180; };

  var gauge = null;
  function buildGauge() {
    var s = mk('svg');
    s.setAttribute('viewBox', '0 0 260 150');
    s.setAttribute('class', 'sw-gauge-svg');
    s.setAttribute('role', 'img');

    // Faint band arcs so the number has context: a reader who has never heard of
    // Kp can still see that the value sits in the green third and where "storm"
    // begins. Each band is drawn edge-to-edge, no gaps, so the ring reads as one
    // scale rather than seven separate arcs.
    BANDS.forEach(function (b, i) {
      var from = i === 0 ? 0 : BANDS[i - 1].to;
      if (from >= 9) return;
      var p = mk('path');
      p.setAttribute('d', arcPath(CX, CY, R, degFor(from), degFor(Math.min(9, b.to))));
      p.setAttribute('class', 'sw-gauge-band');
      p.setAttribute('stroke', cssVar('--sw-' + b.key, '#2fb98a'));
      s.appendChild(p);
    });

    // One long tick at Kp 5 — the storm threshold, the only boundary that changes
    // the answer on this page.
    var a5 = degFor(5);
    var p1 = polar(CX, CY, R - 12, a5), p2 = polar(CX, CY, R + 9, a5);
    var ln = mk('line');
    ln.setAttribute('x1', p1[0].toFixed(1)); ln.setAttribute('y1', p1[1].toFixed(1));
    ln.setAttribute('x2', p2[0].toFixed(1)); ln.setAttribute('y2', p2[1].toFixed(1));
    ln.setAttribute('class', 'sw-gauge-tick major');
    s.appendChild(ln);
    var lbl = mk('text');
    var lp = polar(CX, CY, R + 17, a5);
    lbl.setAttribute('x', lp[0].toFixed(1)); lbl.setAttribute('y', lp[1].toFixed(1));
    lbl.setAttribute('text-anchor', 'middle');
    lbl.setAttribute('class', 'sw-gauge-end');
    lbl.textContent = 'G1';
    s.appendChild(lbl);

    var val = mk('path'); val.setAttribute('class', 'sw-gauge-val'); val.id = 'swGaugeVal';
    s.appendChild(val);
    var tip = mk('circle');
    tip.setAttribute('r', 6); tip.setAttribute('class', 'sw-gauge-tip'); tip.id = 'swGaugeTip';
    s.appendChild(tip);
    return s;
  }
  // `kp === null` means "we do not know", which is NOT the same as Kp 0 and must
  // not be drawn as a green dial sitting in the quiet band.
  function renderGauge(kp) {
    var host = $('swGauge');
    if (!host) return;
    if (!gauge) { gauge = buildGauge(); host.appendChild(gauge); }
    var known = kp != null && !isNaN(kp);
    var deg = degFor(known ? kp : 0);
    var col = known ? kpColor(kp) : 'transparent';
    var p = $('swGaugeVal');
    p.setAttribute('d', arcPath(CX, CY, R, 180, deg));
    p.setAttribute('stroke', col);
    var tip = $('swGaugeTip'), tp = polar(CX, CY, R, deg);
    tip.setAttribute('cx', tp[0].toFixed(1));
    tip.setAttribute('cy', tp[1].toFixed(1));
    tip.setAttribute('fill', col);
    var num = $('swKpNum');
    num.textContent = known ? kpText(kp) : '—';
    num.style.color = known ? kpInk(kp) : 'var(--lg-text-3)';
    gauge.setAttribute('aria-label', known ? 'Kp ' + kpText(kp) : tr('data_unavailable'));
  }

  /**
   * Everything the page must NOT assert while the feed is down.
   *
   * The old error path only reddened a 8px dot in the corner and left the rest of
   * the hero exactly as it had booted: a green dial reading 0 under the headline
   * "No geomagnetic storm — the field is quiet". That is a confident answer given
   * while blind, in the largest type on a page whose entire job is that answer,
   * and it is worse than an error. Same for "NOAA has issued nothing in the last
   * three days" and for the ladder rung marked "now".
   */
  function renderBlind() {
    renderGauge(null);
    var head = $('swVerdict');
    if (head) { head.textContent = tr('unknown_title'); head.style.color = ''; }
    var sub = $('swVerdictSub');
    if (sub) sub.textContent = tr('unknown_sub');
    ['swWhen', 'swEst', 'swMax24'].forEach(function (id) { var e = $(id); if (e) e.hidden = true; });
    var sc = $('swScales'); if (sc) sc.innerHTML = '';
    var card = $('swChartCard'); if (card) card.style.display = 'none';
    var ladder = $('swLadder');
    if (ladder) {
      ladder.querySelectorAll('.sw-rung').forEach(function (r) {
        r.classList.remove('now');
        var b = r.querySelector('.sw-badge-now'); if (b) b.remove();
      });
    }
    var alerts = $('swAlerts');
    if (alerts) {
      alerts.innerHTML = '';
      var e = document.createElement('p');
      e.className = 'sw-empty';
      e.textContent = tr('data_unavailable');
      alerts.appendChild(e);
    }
    // No hour of a storm and no quiet calendar may be claimed while blind.
    renderHours(null);
    var month = $('swMonthCard'); if (month) month.hidden = true;
  }

  // ── verdict + scales ────────────────────────────────────────────────────
  function scaleName(letter, level) { return tr(letter.toLowerCase() + '_' + level); }

  function renderScales(d) {
    var host = $('swScales'); if (!host) return;
    var st = d.storm || {};
    var rows = [
      { l: 'G', v: st.g | 0, hint: tr('scale_g') },
      { l: 'R', v: st.r | 0, hint: tr('scale_r') },
      { l: 'S', v: st.s | 0, hint: tr('scale_s') }
    ];
    host.innerHTML = '';
    rows.forEach(function (r) {
      var el = document.createElement('span');
      el.className = 'sw-scale' + (r.v > 0 ? ' on' : '');
      if (r.v > 0) el.style.color = cssVar('--sw-g' + Math.min(5, r.v) + '-ink', '');
      el.innerHTML = '<b>' + r.l + r.v + '</b>';
      el.appendChild(document.createTextNode(r.hint + ' · ' + scaleName(r.l, Math.min(5, r.v))));
      el.setAttribute('title', r.hint + ': ' + r.l + r.v + ' — ' + scaleName(r.l, Math.min(5, r.v)));
      host.appendChild(el);
    });

    var m = d.max24, box = $('swMax24');
    if (box) {
      if (m && (m.g || m.r || m.s)) {
        box.hidden = false;
        box.innerHTML = tr('max24') + ' <b>G' + (m.g | 0) + ' · R' + (m.r | 0) + ' · S' + (m.s | 0) + '</b>';
      } else {
        box.hidden = true;
      }
    }
  }

  function renderWhen(d) {
    var box = $('swWhen'), txt = $('swWhenText');
    if (!box || !txt) return;
    if (d.verdict && d.since) {
      txt.innerHTML = tr('since') + ' <b>' + dateTime(d.since) + zoneNote() + '</b>';
      box.hidden = false;
    } else if (!d.verdict && d.next && d.next.t) {
      txt.innerHTML = tr('next_storm') + ' <b>' + dateTime(d.next.t) + zoneNote() + '</b> · Kp ' + kpText(d.next.kp);
      box.hidden = false;
    } else if (!d.verdict && d.forecast && d.forecast.length) {
      txt.textContent = tr('no_storm_ahead');
      box.hidden = false;
    } else {
      box.hidden = true;
    }
  }

  // ── "now" dashboard ─────────────────────────────────────────────────────
  function renderNow(d) {
    lastNow = d;
    // Blind is not "quiet": without a Kp AND without a NOAA G level there is no
    // honest verdict to render, whether the payload errored or merely came back
    // partial. A G level alone is still an answer, so that case is not blind.
    var haveKp = d && !d.err && d.kp && d.kp.now != null;
    var haveG = d && !d.err && d.storm && d.storm.g != null;
    if (!haveKp && !haveG) {
      $('swDot').classList.add('err');
      $('swUpd').textContent = tr('data_unavailable');
      renderBlind();
      return;
    }
    var card = $('swChartCard'); if (card) card.style.display = '';
    var kp = haveKp ? d.kp.now : null;
    renderGauge(kp);

    var storm = !!d.verdict;
    var g = (d.storm && d.storm.g) ? d.storm.g : gLevel(kp || 0);
    var head = $('swVerdict');
    head.textContent = storm ? tr('verdict_yes') : tr('verdict_no');
    head.style.color = storm ? kpInk(Math.max(kp || 0, 4 + g)) : '';
    $('swVerdictSub').textContent = storm
      ? ('G' + g + ' · ' + scaleName('G', Math.min(5, g)))
      : (kp >= 4 ? tr('kp_active') : kp >= 3 ? tr('kp_unsettled') : tr('quiet_sub'));

    // The dial shows NOAA's running three-hour estimate. The one-minute feed is a
    // noisier estimate of the same thing — showing both put "3.7" and "0" side by
    // side and made the page argue with itself — so it stays in the payload as a
    // verdict safety-net (it can only RAISE the storm answer) and off the screen.
    var estBox = $('swEst');
    if (estBox) {
      var kind = d.kp && d.kp.kind;
      estBox.hidden = kind !== 'estimated';
      if (kind === 'estimated') estBox.textContent = tr('kp_estimated');
    }

    renderScales(d);
    renderWhen(d);
    renderHours(d);
    renderMonth(d);
    renderWind(d);
    renderChart(d);
    renderSun(d);
    renderAlerts(d);
    highlightLadder(kp);   // null → no rung claims to be "now"

    $('swDot').classList.remove('err');
    $('swDot').classList.toggle('stale', !!d.stale);
    var when = d.kp && d.kp.time ? agoText(d.kp.time) : '';
    $('swUpd').textContent = (d.stale ? tr('cached') + ' · ' : '') + (when || tr('live'));
  }

  function setTile(id, value, state, stateColor) {
    var el = $(id); if (!el) return;
    var v = el.querySelector('.sw-tile-v'); if (v) v.textContent = value;
    var s = el.querySelector('.sw-tile-s');
    if (s) { s.textContent = state || ''; s.style.color = stateColor || 'var(--lg-text-3)'; }
  }

  // ── solar wind + sparklines ─────────────────────────────────────────────
  function sparkline(id, values, opts) {
    var el = $(id); if (!el) return;
    var svg = el.querySelector('.sw-spark'); if (!svg) return;
    var pts = values.filter(function (v) { return v != null && !isNaN(v); });
    svg.innerHTML = '';
    if (pts.length < 2) return;
    var min = Math.min.apply(null, pts), max = Math.max.apply(null, pts);
    if (opts && opts.zero) { min = Math.min(min, 0); max = Math.max(max, 0); }
    var pad = (max - min) * 0.15 || 1;
    min -= pad; max += pad;
    var y = function (v) { return 24 - ((v - min) / (max - min)) * 22; };
    if (opts && opts.zero && min < 0 && max > 0) {
      var z = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      z.setAttribute('class', 'zero');
      z.setAttribute('d', 'M0 ' + y(0).toFixed(1) + ' H100');
      svg.appendChild(z);
    }
    var step = 100 / (pts.length - 1);
    var dstr = pts.map(function (v, i) {
      return (i ? 'L' : 'M') + (i * step).toFixed(1) + ' ' + y(v).toFixed(1);
    }).join(' ');
    var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', dstr);
    svg.appendChild(p);
    svg.style.color = (opts && opts.color) || 'var(--lg-accent)';
  }

  function renderWind(d) {
    var w = d.wind || {};
    var series = d.windSeries || [];
    var col = function (i) { return series.map(function (r) { return r[i]; }); };

    setTile('swWSpeed', w.speed != null ? fmt(w.speed) : '—',
      w.speed == null ? '' : (w.speed >= 600 ? tr('speed_fast') : w.speed >= 450 ? tr('speed_normal') : tr('speed_slow')),
      w.speed >= 600 ? cssVar('--sw-g2-ink', '') : '');
    sparkline('swWSpeed', col(1), { color: 'var(--lg-accent)' });

    setTile('swWDensity', w.density != null ? fmt(w.density, 1) : '—', '', '');
    sparkline('swWDensity', col(3), { color: 'var(--lg-accent-2)' });

    // Bz is the one number that decides whether the wind couples to the
    // magnetosphere at all — southward (negative) opens the door.
    setTile('swWBz', w.bz != null ? (w.bz > 0 ? '+' : '') + fmt(w.bz, 1) : '—',
      w.bz == null ? '' : (w.bz <= -5 ? tr('bz_south') : w.bz >= 1 ? tr('bz_north') : ''),
      w.bz != null && w.bz <= -5 ? cssVar('--sw-quiet-ink', '') : '');
    sparkline('swWBz', col(2), { color: 'var(--lg-accent)', zero: true });

    setTile('swWBt', w.bt != null ? fmt(w.bt, 1) : '—', '', '');
    sparkline('swWBt', col(4), { color: 'var(--lg-accent-2)' });
  }

  // ── the Sun ─────────────────────────────────────────────────────────────
  function renderSun(d) {
    var x = d.xray || {};
    var isBig = x['class'] && /^[MX]/.test(x['class']);
    setTile('swXray', x['class'] || '—',
      d.flare && d.flare['class'] ? tr('flare_last') + ' ' + d.flare['class'] : '',
      isBig ? cssVar('--sw-g2-ink', '') : '');

    var f = d.flares24;
    if (f) {
      // "none" with "↑ B7.6" underneath said two different things at once. The
      // tally counts C/M/X because those are the classes that matter; a quiet day
      // of B-flares is not "none", so it reports its strongest one instead.
      var counted = (f.c | 0) + (f.m | 0) + (f.x | 0);
      var top = f.top && f.top['class'] ? f.top['class'] : null;
      if (counted) {
        setTile('swFlares', 'C' + (f.c | 0) + ' · M' + (f.m | 0) + ' · X' + (f.x | 0),
          top ? tr('flare_strongest') + ' ' + top : '',
          (f.x | 0) ? cssVar('--sw-g4-ink', '') : (f.m | 0) ? cssVar('--sw-g2-ink', '') : '');
      } else if (top) {
        setTile('swFlares', top, tr('flare_strongest_only'), '');
      } else {
        setTile('swFlares', tr('flare_none'), '', '');
      }
    }

    var s = d.sun || {};
    setTile('swF107', s.f107 != null ? s.f107 : '—', s.f107 != null ? 'sfu' : '', '');
    if (s.regions != null) {
      setTile('swSpots', s.regions + ' / ' + (s.spots | 0),
        (s.m_prob != null ? 'M ' + s.m_prob + '%' : '') + (s.x_prob != null ? ' · X ' + s.x_prob + '%' : ''), '');
    }
    var dt = $('swSunDate');
    if (dt && s.date) dt.textContent = dateOnly(s.date);
  }

  // ── NOAA alerts, built from structured fields (never NOAA's English prose) ──
  function alertTitle(a) {
    var scale = a.scale && a.level != null ? ' (' + a.scale + a.level + ' · ' + scaleName(a.scale, a.level) + ')' : '';
    if (a.family === 'kp' && a.k != null) {
      return (a.kind === 'warning' || a.kind === 'watch')
        ? tr('alert_kp_expected').replace(':k', a.k) + scale
        : tr('alert_kp').replace(':k', a.k) + scale;
    }
    var byFamily = {
      proton: 'alert_proton', radio: 'alert_radio', efield: 'alert_efield', xray: 'alert_xray'
    };
    return tr(byFamily[a.family] || 'alert_other') + scale;
  }
  function renderAlerts(d) {
    var host = $('swAlerts'); if (!host) return;
    var list = d.alerts || [];
    if (!list.length) {
      host.innerHTML = '<p class="sw-empty"></p>';
      host.firstChild.textContent = tr('alert_none');
      return;
    }
    host.innerHTML = '';
    list.forEach(function (a) {
      var row = document.createElement('div');
      row.className = 'sw-alert';
      var b = document.createElement('span');
      b.className = 'sw-alert-b';
      b.textContent = tr('alert_kind_' + a.kind) || a.kind;
      var tone = a.level ? '--sw-g' + Math.min(5, a.level) + '-ink'
        : (a.kind === 'warning' || a.kind === 'watch') ? '--sw-g1-ink' : '--lg-text-3';
      b.style.color = cssVar(tone, '');
      var t = document.createElement('div');
      t.className = 'sw-alert-t';
      t.appendChild(document.createTextNode(alertTitle(a)));
      var time = document.createElement('time');
      time.dateTime = a.issued || '';
      time.textContent = dateTime(a.issued) + ' · ' + agoText(a.issued);
      t.appendChild(time);
      row.appendChild(b); row.appendChild(t);
      host.appendChild(row);
    });
  }

  // ── time helpers ────────────────────────────────────────────────────────
  function parseT(iso) {
    if (!iso) return NaN;
    // NOAA hands out both "2026-08-09T02:48:00Z" and "2026-08-09 02:48:00"
    // (the latter already UTC but unmarked) — normalise both to a UTC instant.
    var s = String(iso).replace(' ', 'T');
    if (!/[zZ]|[+-]\d\d:?\d\d$/.test(s)) s += 'Z';
    return Date.parse(s);
  }
  function agoText(iso) {
    var t = parseT(iso);
    if (isNaN(t)) return '';
    var sec = Math.max(0, (Date.now() - t) / 1000);
    if (sec < 90) return tr('live');
    var m = Math.round(sec / 60);
    if (m < 60) return m + ' ' + tr('min_ago');
    var h = Math.round(m / 60);
    if (h < 36) return h + ' ' + tr('h_ago');
    return Math.round(h / 24) + ' ' + tr('d_ago');
  }
  // Every clock on the page belongs to the chosen PLACE (public/js/place-time.js),
  // and to the device until a place is chosen. Kp is one index for the whole
  // planet, so the hour a storm starts is the one thing a city changes.
  function intlLoc() {
    try { if (window.__intlLocale) return window.__intlLocale(); } catch (e) {}
    return C.locale;
  }
  function zone() {
    if (!place) return null;
    if (place.tz) return place.tz;
    return PT ? PT.zoneOf(place.lat, place.lon) : null;
  }
  function fmtAt(t, opts, tz) {
    var d = new Date(t);
    if (PT) return PT.format(d, tz === undefined ? zone() : tz, intlLoc(), opts);
    try { return d.toLocaleString(intlLoc(), opts); } catch (e) { return d.toISOString().slice(0, 16).replace('T', ' '); }
  }
  /** Minutes the place's wall clock is ahead of UTC at instant t. */
  function offAt(t) {
    return PT ? PT.offsetMinutes(zone(), new Date(t)) : -new Date(t).getTimezoneOffset();
  }
  /** "YYYY-MM-DD" of instant t on the place's calendar. */
  function dayKey(t) { return new Date(t + offAt(t) * 60000).toISOString().slice(0, 10); } // utc-calendar: the instant is already shifted to the PLACE's clock (not the device's — __localYmd would be wrong here)
  function nextDayKey(key) { return new Date(Date.parse(key + 'T12:00:00Z') + 86400000).toISOString().slice(0, 10); } // utc-calendar: date arithmetic on a "YYYY-MM-DD" key, noon UTC keeps it off any boundary
  /** "MSK" / "GMT+3" — without it any time on screen is ambiguous. */
  function zoneLabel() { return PT ? PT.label(zone(), new Date(), intlLoc()) : ''; }
  /** " (MSK)" after a hero time, but only when the place's clock is not the device's. */
  function zoneNote() {
    if (!place || !PT) return '';
    var now = Date.now();
    return offAt(now) !== -new Date(now).getTimezoneOffset() ? ' (' + zoneLabel() + ')' : '';
  }

  function dateTime(iso) {
    var t = parseT(iso);
    if (isNaN(t)) return '';
    return fmtAt(t, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }
  function dateOnly(iso) {
    var t = parseT(iso);
    if (isNaN(t)) return '';
    return fmtAt(t, { day: 'numeric', month: 'short' });
  }
  function hourOnly(iso) {
    var t = parseT(iso);
    if (isNaN(t)) return '';
    return fmtAt(t, { hour: '2-digit', minute: '2-digit' });
  }

  // ── Kp chart ────────────────────────────────────────────────────────────
  // Gridlines are positioned by the SAME kp→height formula the bars use. The
  // previous version hardcoded the G1 line at `bottom:44.4%`, which on a 0–9
  // scale is Kp 4 — the storm threshold was drawn one whole step below the storm.
  var pctFor = function (kp) { return (Math.max(0, Math.min(9, kp)) / 9) * 100; };

  // Three kinds of row on ONE timeline: settled observations, NOAA's estimates
  // for the periods of the current day, and the three-day forecast — laid on a
  // real 3-hourly UTC axis. Butting the series end-to-end (what the chart did
  // before) silently closed a ~21-hour hole and put the "now" marker wherever the
  // join happened to land. The chart and the hour grid both read this.
  var STEP = 3 * 3600 * 1000;
  function slotsOf(d) {
    var rows = [];
    (d.kpHistory || []).forEach(function (x) { rows.push({ t: x.t, kp: x.kp, kind: 'o' }); });
    (d.estimated || []).forEach(function (x) { rows.push({ t: x.t, kp: x.kp, kind: 'e' }); });
    (d.forecast || []).forEach(function (x) { rows.push({ t: x.t, kp: x.kp, kind: 'p' }); });
    var byTime = {};
    var min = Infinity, max = -Infinity;
    rows.forEach(function (r) {
      var t = parseT(r.t);
      if (isNaN(t)) return;
      t = Math.floor(t / STEP) * STEP;
      // later kinds win a tie: an estimate supersedes yesterday's observation of
      // the same slot, a forecast never overwrites a measurement of it
      if (!byTime[t] || (byTime[t].kind === 'p' && r.kind !== 'p')) byTime[t] = { kp: r.kp, kind: r.kind };
      if (t < min) min = t;
      if (t > max) max = t;
    });
    return { byTime: byTime, min: min, max: max };
  }

  function renderChart(d) {
    var host = $('swChart'); if (!host) return;
    var wrap = host.parentNode;
    var card = $('swChartCard');
    var s = slotsOf(d);
    var byTime = s.byTime, min = s.min, max = s.max;
    if (!isFinite(min)) { if (card) card.style.display = 'none'; return; }
    if (card) card.style.display = '';
    if (wrap) wrap.style.display = '';

    var grid = $('swGrid');
    if (grid) {
      grid.innerHTML = '';
      [[5, 'G1'], [7, 'G3'], [9, 'G5']].forEach(function (g) {
        var el = document.createElement('span');
        el.style.bottom = pctFor(g[0]) + '%';
        el.setAttribute('data-l', g[1]);
        grid.appendChild(el);
      });
    }

    host.innerHTML = '';
    var slots = Math.round((max - min) / STEP) + 1;
    var lastDay = '';
    for (var i = 0; i < slots; i++) {
      var t = min + i * STEP;
      var cell = document.createElement('div');
      cell.className = 'sw-bar-wrap';
      var row = byTime[t];
      if (row) {
        var bar = document.createElement('div');
        bar.className = 'sw-bar' + (row.kind === 'p' ? ' pred' : row.kind === 'e' ? ' est' : '');
        bar.style.height = Math.max(2, pctFor(row.kp)) + '%';
        if (row.kind === 'p') { bar.style.borderColor = kpColor(row.kp); bar.style.color = kpColor(row.kp); }
        else { bar.style.background = kpColor(row.kp); }
        bar.title = tr('kp_at').replace(':kp', kpText(row.kp)).replace(':time', dateTime(new Date(t).toISOString())) +
          ' · ' + tr(row.kind === 'p' ? 'forecast' : row.kind === 'e' ? 'kp_estimated' : 'observed');
        cell.appendChild(bar);
      }
      var day = dayKey(t);
      if (day !== lastDay) {
        lastDay = day;
        var lb = document.createElement('span');
        lb.className = 'sw-day';
        lb.textContent = dateOnly(new Date(t).toISOString());
        cell.appendChild(lb);
      }
      host.appendChild(cell);
    }

    // "now" sits at its true position on the axis, not at a series boundary.
    var span = (max + STEP) - min;
    var frac = (Date.now() - min) / span;
    if (frac > 0.01 && frac < 0.99) {
      var line = document.createElement('div');
      line.className = 'sw-now';
      line.setAttribute('data-l', tr('now_marker'));
      line.style.insetInlineStart = (frac * 100).toFixed(2) + '%';
      host.appendChild(line);
    }

    // A bar strip is invisible to a screen reader; state the shape in words.
    var sr = $('swChartSr');
    if (sr) {
      var mo = 0, mf = 0;
      Object.keys(byTime).forEach(function (k) {
        var r = byTime[k];
        if (r.kind === 'p') mf = Math.max(mf, r.kp); else mo = Math.max(mo, r.kp);
      });
      sr.textContent = tr('chart_sr').replace(':obs', kpText(mo)).replace(':fc', kpText(mf));
    }
  }

  // ── Hour grid: today and the next days by three-hour period, place clock ──
  // The chart shows a bar's hour only in its hover title, which a phone never
  // shows: on a 384 px screen "at what time is the storm" had no answer at all
  // (suggestion of 2026-10-20 from Sukhum). Here every period carries its hour.
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  /** Local start of a period as "15" — or "05:30" where the zone is off the hour. */
  function slotLabel(t) {
    var l = new Date(t + offAt(t) * 60000);
    return pad2(l.getUTCHours()) + (l.getUTCMinutes() ? ':' + pad2(l.getUTCMinutes()) : '');
  }
  function placeName() { return place && place.name ? place.name : tr('place_unnamed'); }
  function renderHoursTz() {
    var hint = $('swHoursTz'); if (!hint) return;
    hint.textContent = (place ? tr('hours_tz_place').replace(':place', placeName()) : tr('hours_tz_device'))
      .replace(':tz', zoneLabel());
  }
  function renderHours(d) {
    var host = $('swHours'); if (!host) return;
    renderHoursTz();
    host.innerHTML = '';
    var s = d ? slotsOf(d) : null;
    if (!s || !isFinite(s.min)) {
      var e = document.createElement('p');
      e.className = 'sw-empty';
      e.textContent = tr('data_unavailable');
      host.appendChild(e);
      return;
    }
    var now = Date.now(), today = dayKey(now), tomorrow = nextDayKey(today);
    var days = [], byDay = {};
    for (var t = s.min; t <= s.max; t += STEP) {
      var k = dayKey(t);
      if (k < today) continue;
      if (!byDay[k]) { byDay[k] = { key: k, t0: t, slots: [] }; days.push(byDay[k]); }
      byDay[k].slots.push({ t: t, row: s.byTime[t] || null });
    }
    // The forecast ends on a UTC boundary, which east of Greenwich falls just
    // after a local midnight: a "day" of one period is the axis, not a day.
    var tail = days[days.length - 1];
    if (days.length > 1 && tail.slots.filter(function (x) { return x.row; }).length < 4) days.pop();

    days.forEach(function (day) {
      var wrap = document.createElement('div');
      wrap.className = 'sw-hday';
      var head = document.createElement('div');
      head.className = 'sw-hday-h';
      var name = document.createElement('b');
      var near = day.key === today ? tr('day_today') : day.key === tomorrow ? tr('day_tomorrow') : '';
      name.textContent = near
        ? near + (C.dir === 'rtl' ? '، ' : ', ') + fmtAt(day.t0, { day: 'numeric', month: 'short' })
        : fmtAt(day.t0, { weekday: 'short', day: 'numeric', month: 'short' });
      var max = -1;
      day.slots.forEach(function (x) { if (x.row && x.row.kp > max) max = x.row.kp; });
      var chip = document.createElement('span');
      chip.className = 'sw-hday-v';
      if (max >= 5) {
        chip.textContent = tr('day_storm').replace(':g', 'G' + gLevel(max)).replace(':name', scaleName('G', gLevel(max)));
        chip.style.color = kpInk(max);
      } else if (max >= 0) {
        chip.textContent = tr('day_quiet');
      }
      head.appendChild(name);
      head.appendChild(chip);

      var grid = document.createElement('div');
      grid.className = 'sw-hgrid';
      grid.setAttribute('role', 'list');
      day.slots.forEach(function (x) {
        var c = document.createElement('div');
        c.className = 'sw-slot';
        c.setAttribute('role', 'listitem');
        var h = document.createElement('span');
        h.className = 'sw-slot-h';
        h.textContent = slotLabel(x.t);
        var v = document.createElement('span');
        v.className = 'sw-slot-v';
        var isNow = x.t <= now && now < x.t + STEP;
        var label = fmtAt(x.t, { hour: '2-digit', minute: '2-digit' }) + '–' + fmtAt(x.t + STEP, { hour: '2-digit', minute: '2-digit' });
        if (x.row) {
          var g = gLevel(x.row.kp);
          v.textContent = kpText(x.row.kp);
          v.style.color = kpInk(x.row.kp);
          c.style.borderTopColor = kpColor(x.row.kp);
          if (g) c.classList.add('storm');
          label += ' · Kp ' + kpText(x.row.kp) + (g ? ' · G' + g + ' ' + scaleName('G', g) : '');
        } else {
          v.textContent = '—';
          label += ' · ' + tr('data_unavailable');
        }
        if (x.t + STEP <= now) c.classList.add('past');
        if (isNow) { c.classList.add('now'); label += ' · ' + tr('now_marker'); }
        c.title = label;
        c.setAttribute('aria-label', label);
        c.appendChild(h);
        c.appendChild(v);
        grid.appendChild(c);
      });
      wrap.appendChild(head);
      wrap.appendChild(grid);
      host.appendChild(wrap);
    });
  }

  // ── 27 days ahead: NOAA's outlook, one cell per UTC day ─────────────────
  function renderMonth(d) {
    var card = $('swMonthCard'), host = $('swMonth');
    if (!card || !host) return;
    var m = d && d.days27;
    if (!m || !m.days || !m.days.length) { card.hidden = true; return; }

    // A day the three-hourly forecast covers is told by IT: that forecast was
    // issued today, the outlook up to a week ago, and two numbers for one day on
    // one page would argue — the hour grid above says 3, the calendar says 5.
    var s = slotsOf(d), slotMax = {}, slotN = {};
    Object.keys(s.byTime).forEach(function (k) {
      var key = new Date(+k).toISOString().slice(0, 10), kp = s.byTime[k].kp; // utc-calendar: the outlook's days are UTC days
      slotN[key] = (slotN[key] || 0) + 1;
      if (slotMax[key] == null || kp > slotMax[key]) slotMax[key] = kp;
    });
    // "Today" is the place's: in Moscow after midnight the UTC day that is still
    // running is already yesterday, and the hour grid above has moved on too.
    var today = dayKey(Date.now());
    var list = [];
    m.days.forEach(function (x) {
      if (!x || x.d < today || x.kp == null) return;
      var fresh = (slotN[x.d] || 0) >= 6;
      list.push({ d: x.d, kp: fresh ? slotMax[x.d] : x.kp, fresh: fresh });
    });
    if (!list.length) { card.hidden = true; return; }
    card.hidden = false;

    host.innerHTML = '';
    var storms = [], month = '';
    list.forEach(function (x) {
      var t = Date.parse(x.d + 'T12:00:00Z');
      var g = gLevel(x.kp);
      // A month gets its own row label: "1 нояб." inside a 45 px cell came out
      // as "1 но…", and the cells carry only the day number.
      if (x.d.slice(0, 7) !== month) {
        month = x.d.slice(0, 7);
        var mh = document.createElement('div');
        mh.className = 'sw-month-m';
        mh.setAttribute('role', 'listitem');
        mh.textContent = fmtAt(t, { month: 'long' }, 'UTC');
        host.appendChild(mh);
      }
      var c = document.createElement('div');
      c.className = 'sw-mday' + (x.fresh ? '' : ' approx') + (g ? ' storm' : '');
      c.setAttribute('role', 'listitem');
      c.style.borderTopColor = kpColor(x.kp);
      var wd = document.createElement('span');
      wd.className = 'sw-mday-w';
      wd.textContent = fmtAt(t, { weekday: 'short' }, 'UTC');
      var dn = document.createElement('span');
      dn.className = 'sw-mday-d';
      dn.textContent = String(+x.d.slice(8));
      var v = document.createElement('span');
      v.className = 'sw-mday-v';
      v.textContent = kpText(x.kp);
      v.style.color = kpInk(x.kp);
      c.appendChild(wd); c.appendChild(dn); c.appendChild(v);
      if (g) {
        var b = document.createElement('span');
        b.className = 'sw-mday-g';
        b.textContent = 'G' + g;
        b.style.color = kpInk(x.kp);
        c.appendChild(b);
        storms.push(fmtAt(t, { day: 'numeric', month: 'short' }, 'UTC') + ' (G' + g + ')');
      }
      var label = fmtAt(t, { weekday: 'long', day: 'numeric', month: 'long' }, 'UTC') + ' · Kp ' + kpText(x.kp) +
        (g ? ' · G' + g + ' ' + scaleName('G', g) : '') + ' · ' + tr(x.fresh ? 'month_fresh' : 'month_approx');
      c.title = label;
      c.setAttribute('aria-label', label);
      host.appendChild(c);
    });

    var sum = $('swMonthSum');
    if (sum) {
      sum.textContent = storms.length ? tr('month_storms').replace(':list', storms.join(', ')) : tr('month_none');
    }
    var iss = $('swMonthIssued');
    if (iss) {
      var it = parseT(m.issued);
      iss.textContent = isNaN(it) ? '' : tr('month_issued').replace(':date', fmtAt(it, { day: 'numeric', month: 'long' }, 'UTC'));
    }
  }

  // ── What the storm means at the chosen place ────────────────────────────
  // Kp is planetary: Sukhum and Moscow get the same forecast, and saying so is
  // the honest answer to «прогноз по месту». What the place does change is the
  // clock (the grid) and whether an aurora can be seen from it at all.
  var RARE_MLAT = 30;   // |geomagnetic latitude| below which an aurora is "practically never"
  function nf1(v) {
    try { return new Intl.NumberFormat(intlLoc(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v); }
    catch (e) { return v.toFixed(1); }
  }
  function renderPlaceNote() {
    var box = $('swPlaceNote'); if (!box) return;
    box.innerHTML = '';
    var add = function (text, cls) {
      var p = document.createElement('p');
      if (cls) p.className = cls;
      p.textContent = text;
      box.appendChild(p);
      return p;
    };
    if (!place) { add(tr('place_prompt'), 'sw-place-prompt'); return; }

    var mlat = GM ? GM.lat(place.lat, place.lon) : null;
    var head = add('', 'sw-place-h');
    var b = document.createElement('b');
    b.textContent = placeName();
    head.appendChild(b);
    var meta = [zoneLabel()];
    if (mlat != null) meta.push(tr('place_mlat').replace(':mlat', nf1(Math.abs(mlat))));
    head.appendChild(document.createTextNode(' · ' + meta.filter(Boolean).join(' · ')));

    add(tr('place_same'));
    if (mlat == null) return;

    var over = GM.kpNeeded(mlat, 0), horiz = GM.kpNeeded(mlat, GM.HORIZON_MARGIN);
    var kpG = function (k) { return k >= 5 ? k + ' (G' + (k - 4) + ')' : String(k); };
    var txt;
    // Below the Kp 9 boundary the table says nothing more, but history does: the
    // great storms (May 2024) reached the mid-latitudes and practically never the
    // tropics, so "rare" and "practically never" are two different sentences.
    if (over === 0) txt = tr('place_aurora_inside');
    else if (horiz === null) txt = tr(Math.abs(mlat) < RARE_MLAT ? 'place_aurora_never' : 'place_aurora_rare');
    else if (horiz === 0) txt = tr('place_aurora_any').replace(':over', kpG(over));
    else if (over === null) txt = tr('place_aurora_low').replace(':horiz', kpG(horiz));
    else txt = tr('place_aurora_kp').replace(':horiz', kpG(horiz)).replace(':over', kpG(over));
    var a = add(txt);
    if (horiz !== null && C.aurora && C.aurora.url) {
      a.appendChild(document.createTextNode(' '));
      var link = document.createElement('a');
      link.href = C.aurora.url;
      link.textContent = C.aurora.title + ' →';
      a.appendChild(link);
    }
  }

  // ── The place: the shared contract of every «по месту» tool ─────────────
  // public/js/outdoor-weather/place.js, handed over by public/js/space-weather/
  // place.js (an ES module; this file is a classic script): a link's
  // ?lat=&lon=&place=, else the last point the person picked in ANY tool (the
  // site-wide memory of public/js/regions/index.js), else this tool's own last
  // place, else the profile city. Only a pick goes to the site-wide memory; a link,
  // the profile and a place reopened from memory carry `src` and do not.
  var PL = null, locUI = null;
  function whenPlace(fn) {
    if (window.__swPlace) { fn(window.__swPlace); return; }
    window.addEventListener('sw:place', function (e) { fn(e.detail); }, { once: true });
  }
  /** loc is the module's shape: {lat, lng, name, tz, src?}. */
  function setPlace(loc, source, opts) {
    if (!loc || !isFinite(loc.lat) || !isFinite(loc.lng)) return;
    // Geolocation and a link carry no zone of their own; the offline zone grid
    // gives the point's, never the browser's — the reader may be looking at a
    // city in another country.
    var tz = loc.tz || (PT ? PT.zoneOf(loc.lat, loc.lng) : null);
    place = { lat: +loc.lat, lon: +loc.lng, name: loc.name || null, tz: tz || null, src: loc.src || null };
    opts = opts || {};
    remember();
    ae('feature_use', { slug: SLUG, feature: 'place_' + (source || 'set') });
    if (locUI && locUI.setExternal && !opts.fromWidget) {
      try { locUI.setExternal(place.lat, place.lon, place.tz, place.name); } catch (e) {}
    }
    if (!place.name) namePlace(place);
    // Once a place is chosen the widget's "for example: …" line is only height.
    var lh = $('swLoc'); if (lh) lh.classList.add('has-place');
    // The aurora readout on the map follows the same place.
    userLL = [place.lat, place.lon];
    if (markerLayer && window.L) {
      markerLayer.clearLayers();
      L.circleMarker(userLL, { radius: 6, color: '#7aa7ff', weight: 2, fillColor: '#7aa7ff', fillOpacity: 0.6 }).addTo(markerLayer);
    }
    nearMe();
    renderPlaceNote();
    renderHoursTz();
    if (lastNow) renderNow(lastNow);
    if (lastFcst) $('swAuroraTime').textContent = tr('forecast_for') + ' ' + hourOnly(lastFcst) + zoneNote();
  }
  /* The link (?lat=&lon=&place=, canonical stays the bare page), this tool's own
     memory and — for a pick only — the site-wide one: all the module's. */
  function remember() {
    if (!PL || !place) return;
    try {
      PL.remember(SLUG, { lat: place.lat, lng: place.lon, name: place.name || '', tz: place.tz || '', src: place.src || undefined });
    } catch (e) {}
  }
  /* Подпись точки, пришедшей геолокацией или ссылкой без имени, — общий модуль
     GeoCity (public/js/geo-city.js): город с именем на языке страницы. */
  function namePlace(p) {
    if (!window.GeoCity) return;
    GeoCity.lookup(p.lat, p.lon, { mode: 'near' }).then(function (it) {
      var s = GeoCity.text(it);
      if (!s || place !== p || p.name) return;
      p.name = s;
      remember();
      renderPlaceNote();
      renderHoursTz();
    }, function () {});
  }
  function startPlace(P) {
    PL = P;
    var start = null;
    try { start = P.fromUrl() || P.saved(SLUG) || P.fromProfile(); } catch (e) {}
    locUI = P.mountPicker($('swLoc'), SLUG, C.loc || {}, start, function (loc) {
      var pick = function (l) { setPlace(l, l.via || 'city', { fromWidget: true }); };
      // a city carries its zone; typed coordinates do not
      if (loc.tz) pick(loc); else P.fillTz(loc).then(pick, function () { pick(loc); });
    });
    if (!start) return;
    // A link (and a profile city) may come without a zone: the module's fillTz
    // decides it — the town's own clock by a town, the zone grid away from towns.
    // Taking the grid directly put Sukhum on Tbilisi time (GMT+4) — it runs on Moscow's.
    var open = function (loc) { if (!place || place.src) setPlace(loc, 'open_' + (loc.src || 'saved')); };
    if (start.tz) open(start);
    else P.fillTz(start).then(open, function () { open(start); });
  }

  // ── Kp ladder highlight ─────────────────────────────────────────────────
  function highlightLadder(kp) {
    var host = $('swLadder'); if (!host) return;
    var rungs = host.querySelectorAll('.sw-rung');
    if (kp == null || isNaN(kp)) {
      rungs.forEach(function (r) {
        r.classList.remove('now');
        var b = r.querySelector('.sw-badge-now'); if (b) b.remove();
      });
      return;
    }
    var target = Math.max(0, Math.min(9, Math.round(kp)));
    var best = null;
    rungs.forEach(function (r) {
      r.classList.remove('now');
      var badge = r.querySelector('.sw-badge-now');
      if (badge) badge.remove();
      var v = parseInt(r.getAttribute('data-kp'), 10);
      if (isNaN(v)) return;
      if (v <= target && (best === null || v > parseInt(best.getAttribute('data-kp'), 10))) best = r;
    });
    if (!best && rungs.length) best = rungs[0];
    if (best) {
      best.classList.add('now');
      var k = best.querySelector('.sw-rung-k');
      if (k) {
        var badge = document.createElement('span');
        badge.className = 'sw-badge-now';
        badge.textContent = tr('now_marker');
        k.appendChild(badge);
      }
    }
  }

  // ── Aurora map ──────────────────────────────────────────────────────────
  var map = null, markerLayer = null, cells = [], userLL = null, hemi = 'N';
  var auroraCanvas = null, auroraTex = null, drawQueued = false;

  function initMap() {
    if (!window.L || !$('swMap')) return;
    var tile = C.tile || {};
    map = L.map('swMap', {
      center: [C.home ? C.home.lat : 64, C.home ? C.home.lon : 20],
      zoom: C.home ? C.home.z : 3, minZoom: 1, maxZoom: 7,
      worldCopyJump: true, attributionControl: true, zoomControl: true
    });
    // Подложка — общий примитив (запасной адрес, растровый откат, строка при
    // отказе). Здесь стоял голый L.tileLayer: упавший хост давал чёрный
    // прямоугольник, и тул об этом не знал.
    if (window.__mapTiles) {
      window.__mapTiles.base(L, map, null, { slug: 'space-weather', style: tile.style || 'dark', credit: tr('map_attr'), opts: { maxZoom: 19 }, text: tile.offline });
    } else if (window.__basemapFallback) {
      window.__basemapFallback(L, map, tr('map_attr'));
    }

    markerLayer = L.layerGroup().addTo(map);
    auroraCanvas = document.createElement('canvas');
    auroraCanvas.className = 'sw-aurora-canvas';
    map.getPanes().overlayPane.appendChild(auroraCanvas);

    // The oval is ~17 000 cells. Painting them synchronously on every 'move'
    // frame is a full repaint per pointer event; coalescing into one rAF keeps a
    // drag at one paint per frame instead of several.
    map.on('move zoom resize viewreset moveend zoomend', queueDraw);
    stage('map_ready');
  }
  function queueDraw() {
    if (drawQueued) return;
    drawQueued = true;
    (window.requestAnimationFrame || function (f) { setTimeout(f, 16); })(function () {
      drawQueued = false;
      drawAurora();
    });
  }

  /**
   * Bake the OVATION grid into ONE Web-Mercator texture per load.
   *
   * Three renderers were tried here. Walking ~17 000 cells and issuing a fillRect
   * each, every pan frame, was thousands of draw calls and gave hard-edged squares
   * — the oval read as a blanket of green tiles, not a glow. Blitting the grid one
   * latitude row at a time fixed the squares but not the cost, and bilinear
   * sampling of a 1-pixel-tall slice fringes at both edges, so the oval came out
   * combed with 181 horizontal seams.
   *
   * Web Mercator is a fixed function of latitude, so the projection does not
   * depend on the view: resample the 1° grid into Mercator rows ONCE, and every
   * frame afterwards is a single drawImage per visible world copy.
   */
  var MERC_H = 1024, MERC_W = 720, MERC_LAT = 85.0511287798066;
  function mercLat(y) { // 0..1 down the Mercator world → latitude
    return (2 * Math.atan(Math.exp((1 - 2 * y) * Math.PI)) - Math.PI / 2) * 180 / Math.PI;
  }

  // Bilinear read of the 1° grid at fractional lon/lat. Nearest-neighbour left
  // hard latitude steps across the oval — visible as a staircase edge — because
  // one source row covers ~11 screen pixels at map zoom 4.
  function sampleGrid(grid, lon, lat) {
    var x = lon + 180, y = 90 - lat;
    var x0 = Math.floor(x), y0 = Math.floor(y);
    var fx = x - x0, fy = y - y0;
    if (y0 < 0) { y0 = 0; fy = 0; }
    if (y0 > 179) { y0 = 179; fy = 1; }
    x0 = ((x0 % 360) + 360) % 360;
    var x1 = (x0 + 1) % 360, y1 = y0 + 1;
    var r0 = y0 * 360, r1 = y1 * 360;
    return (grid[r0 + x0] * (1 - fx) + grid[r0 + x1] * fx) * (1 - fy)
         + (grid[r1 + x0] * (1 - fx) + grid[r1 + x1] * fx) * fy;
  }

  function bakeAurora() {
    // 1° grid → dense lookup, so the Mercator resample below is a plain array read
    var grid = new Uint8Array(360 * 181);
    for (var i = 0; i < cells.length; i++) {
      var x = Math.round(cells[i][0]) + 180;
      var y = 90 - Math.round(cells[i][1]);
      if (x < 0) x += 360; else if (x > 359) x -= 360;
      if (y < 0 || y > 180) continue;
      grid[y * 360 + x] = Math.min(255, cells[i][2]);
    }
    auroraTex = document.createElement('canvas');
    auroraTex.width = MERC_W; auroraTex.height = MERC_H;
    var ctx = auroraTex.getContext('2d');
    var img = ctx.createImageData(MERC_W, MERC_H);
    var px = img.data;
    var rgba = [0, 0, 0, 0];
    for (var row = 0; row < MERC_H; row++) {
      var lat = mercLat((row + 0.5) / MERC_H);
      var out = row * MERC_W * 4;
      for (var col = 0; col < MERC_W; col++) {
        var lon = (col + 0.5) * 360 / MERC_W - 180;
        auroraRGB(sampleGrid(grid, lon, lat), rgba);
        var o = out + col * 4;
        px[o] = rgba[0]; px[o + 1] = rgba[1]; px[o + 2] = rgba[2]; px[o + 3] = rgba[3];
      }
    }
    ctx.putImageData(img, 0, 0);
  }

  /**
   * Value → colour. Two deliberate choices:
   *
   * Alpha starts at ZERO at the 3 % floor. The old renderer used a flat 0.12
   * floor, and the thousands of 3–5 % cells that blanket a third of the globe on a
   * quiet night then painted solid green from Norway to France — which is not what
   * a 4 % chance of aurora looks like.
   *
   * The hue ramp INTERPOLATES rather than stepping at 6/12/22/40: hard stops drew
   * concentric colour bands with visible edges, so the oval looked like a contour
   * map instead of a glow.
   */
  var RAMP = [
    [3, 26, 122, 60], [8, 47, 158, 68], [16, 105, 219, 124],
    [26, 255, 212, 59], [36, 255, 150, 60], [50, 255, 77, 109]
  ];
  function auroraRGB(v, out) {
    if (v < 3) { out[3] = 0; return out; }
    var i = 0;
    while (i < RAMP.length - 2 && v > RAMP[i + 1][0]) i++;
    var a = RAMP[i], b = RAMP[i + 1];
    var f = Math.max(0, Math.min(1, (v - a[0]) / (b[0] - a[0])));
    out[0] = Math.round(a[1] + (b[1] - a[1]) * f);
    out[1] = Math.round(a[2] + (b[2] - a[2]) * f);
    out[2] = Math.round(a[3] + (b[3] - a[3]) * f);
    out[3] = Math.round(Math.pow(Math.max(0, Math.min(1, (v - 3) / 42)), 0.85) * 235);
    return out;
  }

  function drawAurora() {
    if (!map || !auroraCanvas || !cells.length) return;
    if (!auroraTex) bakeAurora();
    var size = map.getSize();
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    var w = Math.round(size.x * dpr), h = Math.round(size.y * dpr);
    if (auroraCanvas.width !== w || auroraCanvas.height !== h) {
      auroraCanvas.width = w; auroraCanvas.height = h;
      auroraCanvas.style.width = size.x + 'px';
      auroraCanvas.style.height = size.y + 'px';
    }
    // align the canvas to the top-left of the map container (overlayPane is translated)
    L.DomUtil.setPosition(auroraCanvas, map.containerPointToLayerPoint([0, 0]));
    var ctx = auroraCanvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.x, size.y);
    ctx.imageSmoothingEnabled = true;

    var tl = map.latLngToContainerPoint([MERC_LAT, -180]);
    var br = map.latLngToContainerPoint([-MERC_LAT, 180]);
    var worldW = br.x - tl.x, worldH = br.y - tl.y;
    if (!isFinite(worldW) || worldW <= 0 || worldH <= 0) return;

    // The world repeats horizontally; paint only the copies that intersect.
    var from = Math.floor((0 - tl.x) / worldW);
    var to = Math.ceil((size.x - tl.x) / worldW);
    for (var k = from; k <= to; k++) {
      ctx.drawImage(auroraTex, tl.x + k * worldW, tl.y, worldW, worldH);
    }
  }

  function loadAurora() {
    fetch(API.aurora, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        cells = (d && d.cells) || [];
        auroraTex = null; // re-bake from the new grid
        queueDraw();
        if (d && d.fcst) {
          lastFcst = d.fcst;
          $('swAuroraTime').textContent = tr('forecast_for') + ' ' + hourOnly(d.fcst) + zoneNote();
        }
        nearMe();
        if (cells.length) stage('aurora_ready');
      })
      .catch(function () { ae('tool_error', { slug: 'space-weather', where: 'aurora' }); });
  }

  // Southern users were served a map they could never appear on: the oval is a
  // pair of rings and only the northern one was ever framed.
  function setHemisphere(h) {
    hemi = h;
    var n = $('swHemiN'), s = $('swHemiS');
    if (n) n.setAttribute('aria-pressed', String(h === 'N'));
    if (s) s.setAttribute('aria-pressed', String(h === 'S'));
    if (!map) return;
    var home = C.home || { lat: 62, lon: 20, z: 3 };
    map.setView(
      // -53, not the oval's own latitude: framing dead-centre on the ring fills
      // the view with ocean and pushes Tasmania and the South Island off the top.
      h === 'S' ? [-53, home.southLon != null ? home.southLon : 147] : [home.lat, home.lon],
      home.z
    );
    ae('feature_use', { slug: 'space-weather', feature: 'hemisphere_' + h });
  }

  function nearMe() {
    if (!userLL || !cells.length) return;
    var best = 0;
    for (var i = 0; i < cells.length; i++) {
      if (Math.abs(cells[i][1] - userLL[0]) <= 3 && Math.abs(cells[i][0] - userLL[1]) <= 4) {
        best = Math.max(best, cells[i][2]);
      }
    }
    var key = best >= 26 ? 'near_high' : best >= 10 ? 'near_moderate' : best >= 3 ? 'near_low' : 'near_none';
    var box = $('swNear'); if (!box) return;
    box.hidden = false;
    box.className = 'sw-near ' + (best >= 10 ? 'on' : '');
    box.innerHTML = '';
    var b = document.createElement('b');
    b.textContent = tr('near_title');
    box.appendChild(b);
    box.appendChild(document.createTextNode(' ' + tr(key) + (best >= 3 ? ' · ' + best + '%' : '')));
  }

  function locate() {
    var b = $('swLocate');
    // Geolocation is the place module's: it disables the button while asking, and
    // the shared __geo explains a refusal (a bare `return` here was once a silent
    // dead click).
    var ask = function (P) {
      P.geolocate(b, function (loc) {
        // Pick the hemisphere from the fix BEFORE flying there: setHemisphere
        // re-frames the map, so doing it after would throw the user back to the
        // default oval a frame later.
        setHemisphere(loc.lat < 0 ? 'S' : 'N');
        if (map) map.setView([loc.lat, loc.lng], Math.max(map.getZoom(), 4));
        // The map's button sets THE place of the page — hour grid, clock and the
        // aurora readout answer for the same point.
        setPlace(loc, 'locate');
      });
    };
    if (PL) { ask(PL); return; }
    // the module is still on its way (or this browser has no modules): say so
    // instead of a dead click
    try { window.__toast && window.__toast(tr('locating'), 'info'); } catch (e) {}
    whenPlace(ask);
  }

  // ── boot ────────────────────────────────────────────────────────────────
  function loadNow() {
    return fetch(API.now, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        renderNow(d);
        if (d && !d.err) stage('data_ready');
      })
      .catch(function () {
        $('swDot').classList.add('err');
        $('swUpd').textContent = tr('data_unavailable');
        ae('tool_error', { slug: 'space-weather', where: 'now' });
      });
  }

  function boot() {
    stage('view');
    renderGauge(null); // not 0 — we have not asked NOAA yet
    initMap();
    renderPlaceNote();
    renderHoursTz();   // whose clock, before the data and the place arrive
    // The place arrives with the shared module (deferred, so usually right after
    // this); until then every clock is the device's.
    whenPlace(startPlace);
    loadNow().then(function () {
      ae('tool_complete', { slug: 'space-weather' });
    });
    loadAurora();
    var lb = $('swLocate'); if (lb) lb.addEventListener('click', locate);
    var hn = $('swHemiN'); if (hn) hn.addEventListener('click', function () { setHemisphere('N'); });
    var hs = $('swHemiS'); if (hs) hs.addEventListener('click', function () { setHemisphere('S'); });

    // Pause polling on a hidden tab: this page used to keep two timers running
    // in a background tab forever.
    var nowTimer = null, auroraTimer = null;
    function startTimers() {
      stopTimers();
      nowTimer = setInterval(loadNow, 60000);
      auroraTimer = setInterval(loadAurora, 150000);
    }
    function stopTimers() {
      if (nowTimer) clearInterval(nowTimer);
      if (auroraTimer) clearInterval(auroraTimer);
      nowTimer = auroraTimer = null;
    }
    startTimers();
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stopTimers(); }
      else { loadNow(); loadAurora(); startTimers(); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
